import * as React from 'react';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Switch from '@mui/material/Switch';
import CircularProgress from '@mui/material/CircularProgress';
import Menu from '@mui/material/Menu';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Chip from '@mui/material/Chip';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import { useTheme } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { getTechnicians, getUsersList, updateUser } from '../../../store/slices/usersSlice';
import { formatUserDisplayName } from '../../../utils/userDisplayName';
import { getUser } from '../../../store/slices/userSlice';
import TechnicianDetailsDialog from './TechnicianDetailsDialog';
import AddTechnicianDialog from '../jobs/AddTechnicianDialog';
import { PUT, GET } from '../../../configs/api';
import { canManageTechnicians, canCreateTechnician } from '../../../utils/permissions';
import { isTechnicianArchived } from '../../../utils/technicianStatus';
import { api, states, REQUIRED_1099_THRESHOLD } from '../../../utils/constants';
import useIsMobile from '../../../hooks/useIsMobile';
import { scaleColumnsForMobile } from '../../../utils/mobileDataGrid';

const paginationModel = { page: 0, pageSize: 30 };

const parseNumericField = (value) => {
  if (value == null || value === '') return 0;
  const n = typeof value === 'string' ? parseFloat(value) : Number(value);
  return Number.isFinite(n) ? n : 0;
};

const getAvgWorkOrderCost = (earnings, jobs) => {
  const numEarnings = parseNumericField(earnings);
  const numJobs = parseNumericField(jobs);
  if (numJobs === 0 || Number.isNaN(numJobs) || Number.isNaN(numEarnings)) return 0;
  const avg = numEarnings / numJobs;
  return Number.isNaN(avg) ? 0 : avg;
};

const getDisplayName = (row) => {
  if (row.type === 'vendor') {
    return row.company_name || '';
  }
  return `${row.first_name || ''} ${row.last_name || ''}`.trim();
};

const matchesTechnicianSearch = (tech, query, displayUserByEmail = (email) => email || '') => {
  const searchableText = [
    getDisplayName(tech),
    tech.first_name,
    tech.last_name,
    tech.company_name,
    tech.email,
    tech.phone,
    tech.address,
    tech.city,
    tech.state,
    tech.travel_distance?.toString(),
    tech.created_by_email,
    displayUserByEmail(tech.created_by_email),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const tokens = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  return tokens.every((token) => searchableText.includes(token));
};

export default function Technicians() {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const technicians = useSelector(getTechnicians);
  const usersList = useSelector(getUsersList);
  const user = useSelector(getUser);

  const displayUserByEmail = React.useMemo(() => {
    const byEmail = new Map(
      (usersList || [])
        .filter((u) => u.email)
        .map((u) => [u.email.toLowerCase(), u])
    );
    return (email) => {
      if (!email) return '';
      const match = byEmail.get(String(email).toLowerCase());
      return match ? formatUserDisplayName(match) : email;
    };
  }, [usersList]);
  const gridApiRef = useGridApiRef();
  const [selectedTechnician, setSelectedTechnician] = React.useState(null);
  const [addTechnicianDialogOpen, setAddTechnicianDialogOpen] = React.useState(false);
  const [initialTechnicianType, setInitialTechnicianType] = React.useState('technician');
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [loadingBlacklist, setLoadingBlacklist] = React.useState({});
  const [activeFilter, setActiveFilter] = React.useState(null); // null, '1099', 'w9', 'blacklisted'
  const [viewType, setViewType] = React.useState('list'); // 'grid' or 'list'
  const [blacklistDialogOpen, setBlacklistDialogOpen] = React.useState(false);
  const [blacklistDialogTechnician, setBlacklistDialogTechnician] = React.useState(null);
  const [blockedReason, setBlockedReason] = React.useState('');
  const [blockedReasonError, setBlockedReasonError] = React.useState('');
  
  // Filter state (all local state)
  const [selectedTypes, setSelectedTypes] = React.useState([]);
  const [selectedStates, setSelectedStates] = React.useState([]);
  const [selectedYear, setSelectedYear] = React.useState(new Date().getFullYear().toString());
  const [yearEarningsData, setYearEarningsData] = React.useState({});
  const [loadingYearEarnings, setLoadingYearEarnings] = React.useState(false);
  const isInitialMount = React.useRef(true);

  // Handle toggle blacklisted status
  const handleToggleBlacklisted = async (technicianId, currentBlacklisted) => {
    // If trying to blacklist (currently not blacklisted), show dialog for reason
    if (!currentBlacklisted) {
      const technician = technicians.find(t => t.id === technicianId);
      setBlacklistDialogTechnician(technician);
      setBlockedReason('');
      setBlockedReasonError('');
      setBlacklistDialogOpen(true);
      return;
    }
    
    // If unblacklisting, proceed directly
    await performToggleBlacklisted(technicianId, false, null);
  };

  // Perform the actual blacklist toggle API call
  const performToggleBlacklisted = async (technicianId, blacklisted, reason) => {
    setLoadingBlacklist(prev => ({ ...prev, [technicianId]: true }));
    try {
      const response = await PUT(`technicians/${technicianId}/blacklisted`, {}, { 
        blacklisted, 
        blocked_reason: reason 
      });
      if (response.data) {
        dispatch(updateUser(response.data));
      }
    } catch (error) {
      console.error('Failed to toggle blacklisted status:', error);
      if (error.response?.data?.message) {
        setBlockedReasonError(error.response.data.message);
      }
    } finally {
      setLoadingBlacklist(prev => ({ ...prev, [technicianId]: false }));
    }
  };

  // Handle blacklist dialog confirm
  const handleBlacklistDialogConfirm = () => {
    // Validate blocked reason
    if (!blockedReason || blockedReason.trim().length < 10) {
      setBlockedReasonError('Blocked reason is required and must be at least 10 characters');
      return;
    }

    if (blacklistDialogTechnician) {
      performToggleBlacklisted(blacklistDialogTechnician.id, true, blockedReason.trim());
      setBlacklistDialogOpen(false);
      setBlacklistDialogTechnician(null);
      setBlockedReason('');
      setBlockedReasonError('');
    }
  };

  // Handle blacklist dialog cancel
  const handleBlacklistDialogCancel = () => {
    setBlacklistDialogOpen(false);
    setBlacklistDialogTechnician(null);
    setBlockedReason('');
    setBlockedReasonError('');
  };

  // Check if user can toggle blacklisted (owner or admin)
  const canToggleBlacklisted = canManageTechnicians(user);

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      width: 160,
      valueGetter: (value, row) => getDisplayName(row),
    },
    {
      field: 'is_active',
      headerName: 'Archived',
      width: 110,
      sortable: true,
      valueGetter: (value, row) => (isTechnicianArchived(row) ? 'Archived' : 'Active'),
      renderCell: (params) => {
        const isArchived = isTechnicianArchived(params.row);
        if (!isArchived) {
          return (
            <Chip
              label="Active"
              size="small"
              sx={{
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 600,
                backgroundColor: isDarkMode ? '#1b4332' : '#E8F5E9',
                color: isDarkMode ? '#95d5b2' : '#2e7d32',
              }}
            />
          );
        }
        return (
          <Chip
            label="Archived"
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              fontWeight: 600,
              backgroundColor: isDarkMode ? '#475569' : '#e0e0e0',
              color: isDarkMode ? '#e2e8f0' : '#616161',
            }}
          />
        );
      },
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 100,
      renderCell: (params) => {
        const isVendor = params.row.type === 'vendor';
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <Box
              sx={{
                padding: '2px 8px',
                borderRadius: '12px',
                backgroundColor: isVendor ? '#E3F2FD' : '#F3E5F5',
                border: `1px solid ${isVendor ? '#90CAF9' : '#CE93D8'}`,
                color: isVendor ? '#1565C0' : '#7B1FA2',
                fontWeight: 500,
                fontSize: '0.75rem',
                whiteSpace: 'nowrap',
                lineHeight: 1.2,
              }}
            >
              {isVendor ? 'Vendor' : 'Technician'}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 120,
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 150,
    },
    {
      field: 'location',
      headerName: 'Location',
      width: 175,
      valueGetter: (value, row) => {
        const parts = [];
        if (row.address) parts.push(row.address);
        if (row.city) parts.push(row.city);
        if (row.state) parts.push(row.state);
        return parts.join(', ');
      }
    },
    {
      field: 'travel_distance',
      headerName: 'Travel Distance',
      width: 120,
      valueFormatter: (value) => value ? `${value} mi` : '',
    },
    {
      field: 'total_earnings',
      headerName: 'Total Earnings',
      width: 120,
      type: 'number',
      valueGetter: (value, row) => parseNumericField(row.total_earnings),
      valueFormatter: (value) => {
        if (value == null || Number.isNaN(value)) return '$0';
        return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      },
      renderCell: (params) => {
        const numValue = params.value == null || Number.isNaN(params.value) ? 0 : params.value;
        const formattedValue = `$${numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        const isGreen = numValue >= REQUIRED_1099_THRESHOLD;

        return (
          <Box sx={{ fontWeight: 600, color: isGreen ? '#2e7d32' : 'text.primary' }}>
            {formattedValue}
          </Box>
        );
      },
    },
    {
      field: 'total_jobs',
      headerName: 'Total Work Orders',
      width: 140,
      type: 'number',
      valueGetter: (value, row) => parseNumericField(row.total_jobs),
    },
    {
      field: 'avg_work_order_cost',
      headerName: 'Avg Cost',
      width: 120,
      type: 'number',
      valueGetter: (value, row) => getAvgWorkOrderCost(row.total_earnings, row.total_jobs),
      valueFormatter: (value) => {
        if (value == null || Number.isNaN(value)) return '$0.00';
        return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      },
    },
    {
      field: '1099_required',
      headerName: '1099 Required',
      width: 120,
      valueGetter: (value, row) => {
        const earnings = row.total_earnings;
        const numEarnings = earnings == null || earnings === undefined ? 0 : (typeof earnings === 'string' ? parseFloat(earnings) : earnings);
        return numEarnings > REQUIRED_1099_THRESHOLD ? 'Required' : 'Not required';
      },
    },
    {
      field: 'w9',
      headerName: 'W-9 Status',
      width: 120,
      renderCell: (params) => {
        const hasW9 = params.row.w9 || false;
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <Box
              sx={{
                padding: '2px 8px',
                borderRadius: '12px',
                backgroundColor: hasW9 ? '#8FC98F' : '#FFA8A8',
                border: `1px solid ${hasW9 ? '#5A985A' : '#FF7070'}`,
                color: '#fff',
                fontWeight: 500,
                fontSize: '0.75rem',
                whiteSpace: 'nowrap',
                lineHeight: 1.2,
              }}
            >
              {hasW9 ? 'W-9 On File' : 'Missing W-9'}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'blacklisted',
      headerName: 'Blacklisted',
      width: 120,
      renderCell: (params) => {
        const isLoading = loadingBlacklist[params.row.id];
        const isBlacklisted = params.row.blacklisted || false;
        
        if (isLoading) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, width: '100%', height: '100%' }}>
              <CircularProgress size={20} />
            </Box>
          );
        }
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, width: '100%', height: '100%' }}>
            <Switch
              checked={isBlacklisted}
              onChange={() => handleToggleBlacklisted(params.row.id, isBlacklisted)}
              disabled={!canToggleBlacklisted}
              size="small"
              onClick={(e) => e.stopPropagation()}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: isDarkMode ? '#fff' : '#000',
                  '& + .MuiSwitch-track': {
                    backgroundColor: isDarkMode ? '#666' : '#666',
                  },
                },
                '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
                  backgroundColor: isDarkMode ? '#fff' : '#000',
                },
                '& .MuiSwitch-track': {
                  backgroundColor: isDarkMode ? '#475569' : '#ccc',
                },
              }}
            />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {isBlacklisted ? 'On' : 'Off'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'created_by_email',
      headerName: 'Created By',
      width: 140,
      valueFormatter: (value) => displayUserByEmail(value),
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 130,
      valueFormatter: (value) => value ? value.slice(0, 10) : '',
    },
  ];

  const displayColumns = React.useMemo(
    () => (isMobile ? scaleColumnsForMobile(columns) : columns),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isMobile, isDarkMode, loadingBlacklist, canToggleBlacklisted],
  );

  // Display technician details dialog
  const handleRowSelectionModelChange = (newRowSelectionModel) => {
    if (newRowSelectionModel.ids.size > 0) {
      const technicianId = newRowSelectionModel.ids.values().next().value;
      const selectedTechnician = technicians.find(t => t.id === technicianId);
      if (selectedTechnician) setSelectedTechnician(selectedTechnician);
    }
  };

  // Handle opening add technician dialog with type
  const handleOpenAddDialog = (type = 'technician') => {
    setInitialTechnicianType(type);
    setAddTechnicianDialogOpen(true);
  };

  // Handle dropdown menu
  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuSelect = (type) => {
    handleMenuClose();
    handleOpenAddDialog(type);
  };

  // Calculate summary counts (using year earnings data if available)
  const summaryCounts = React.useMemo(() => {
    const total = technicians.length || 0;
    
    const count1099Required = technicians.filter(tech => {
      // Use year earnings if available, otherwise use Redux earnings
      const earningsData = yearEarningsData[tech.id];
      const earnings = earningsData ? earningsData.total_earnings : tech.total_earnings;
      const numEarnings = earnings == null || earnings === undefined ? 0 : (typeof earnings === 'string' ? parseFloat(earnings) : earnings);
      return numEarnings > REQUIRED_1099_THRESHOLD;
    }).length;
    
    const countNeedW9 = technicians.filter(tech => !tech.w9 || tech.w9 === false).length;
    
    const countBlacklisted = technicians.filter(tech => tech.blacklisted === true).length;
    const countArchived = technicians.filter(tech => isTechnicianArchived(tech)).length;
    
    return {
      total,
      count1099Required,
      countNeedW9,
      countBlacklisted,
      countArchived,
    };
  }, [technicians, yearEarningsData]);

  // Fetch year earnings when selectedYear changes (skip initial mount)
  React.useEffect(() => {
    // Skip API call on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const fetchYearEarnings = async () => {
      setLoadingYearEarnings(true);
      try {
        const response = await GET(api.getTechniciansEarningsByYear, { year: selectedYear });
        if (response.data && !response.error) {
          // Store earnings data as object keyed by technician ID
          const earningsMap = {};
          response.data.forEach(tech => {
            earningsMap[tech.id] = {
              ...tech,
              total_earnings: tech.total_earnings
            };
          });
          setYearEarningsData(earningsMap);
        }
      } catch (error) {
        console.error('Failed to fetch year earnings:', error);
      } finally {
        setLoadingYearEarnings(false);
      }
    };

    fetchYearEarnings();
  }, [selectedYear]);

  // Generate year options (current year and past 5 years)
  const yearOptions = React.useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i <= 5; i++) {
      years.push((currentYear - i).toString());
    }
    return ['all', ...years];
  }, []);

  // Get available states - use 2-letter codes (keys) from constants, sorted by state name
  const availableStates = React.useMemo(() => {
    return Object.keys(states).sort((a, b) => states[a].localeCompare(states[b]));
  }, []);

  // Handle filter click
  const handleFilterClick = (filterType) => {
    if (activeFilter === filterType) {
      // If clicking the same filter, deselect it
      setActiveFilter(null);
    } else {
      setActiveFilter(filterType);
    }
  };

  // Handle type filter toggle
  const handleTypeToggle = (type) => {
    setSelectedTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  // Filter technicians based on search query, active filter, and new filters
  const filteredTechnicians = React.useMemo(() => {
    let filtered = technicians;
    
    // Apply type filter
    if (selectedTypes.length > 0) {
      filtered = filtered.filter(tech => selectedTypes.includes(tech.type));
    }
    
    // Apply state filter
    if (selectedStates.length > 0) {
      filtered = filtered.filter(tech => selectedStates.includes(tech.state));
    }
    
    // Apply active filter
    if (activeFilter === '1099') {
      filtered = filtered.filter(tech => {
        // Use year earnings if available, otherwise use Redux earnings
        const earningsData = yearEarningsData[tech.id];
        const earnings = earningsData ? earningsData.total_earnings : tech.total_earnings;
        const numEarnings = earnings == null || earnings === undefined ? 0 : (typeof earnings === 'string' ? parseFloat(earnings) : earnings);
        return numEarnings > REQUIRED_1099_THRESHOLD;
      });
    } else if (activeFilter === 'w9') {
      filtered = filtered.filter(tech => !tech.w9 || tech.w9 === false);
    } else if (activeFilter === 'blacklisted') {
      filtered = filtered.filter(tech => tech.blacklisted === true);
    } else if (activeFilter === 'archived') {
      filtered = filtered.filter(tech => isTechnicianArchived(tech));
    }
    
    // Apply search query (supports multi-word queries like "cl pl" for "cl plumbing")
    if (searchQuery) {
      filtered = filtered.filter((tech) => matchesTechnicianSearch(tech, searchQuery, displayUserByEmail));
    }
    
    // Merge year earnings data into filtered results
    return filtered.map(tech => {
      const earningsData = yearEarningsData[tech.id];
      if (earningsData) {
        return {
          ...tech,
          total_earnings: earningsData.total_earnings,
          total_jobs: earningsData.total_jobs,
        };
      }
      return tech;
    });
  }, [technicians, searchQuery, activeFilter, selectedTypes, selectedStates, yearEarningsData, displayUserByEmail]);

  // Handle card click to open details
  const handleCardClick = (technician) => {
    setSelectedTechnician(technician);
  };

  // Handle edit icon click
  const handleEditClick = (e, technician) => {
    e.stopPropagation();
    setSelectedTechnician(technician);
  };

  // Handle columns panel toggle
  const handleColumnsPanelToggle = () => {
    if (gridApiRef.current) {
      // Use showPreferences to open the columns panel
      // This works in MUI DataGrid v6+
      try {
        gridApiRef.current.showPreferences('columns');
      } catch (error) {
        console.warn('Columns panel API method not available:', error);
      }
    }
  };


  // Error message
  if (technicians.error) {
    return (
      <Box sx={{
        color: 'text.primary',
        bgcolor: 'background.paper',
        padding: '24px',
        borderRadius: '8px',
        margin: '40px auto',
        maxWidth: '400px',
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: '1.2rem',
        boxShadow: isDarkMode ? '0 2px 8px rgba(211,47,47,0.3)' : '0 2px 8px rgba(211,47,47,0.15)'
      }}>
        <span style={{ fontSize: '2rem', display: 'block', marginBottom: '12px' }}>⚠️</span>
        {technicians.error}
      </Box>
    );
  }

  return (
    <>
      <Paper 
        elevation={0}
        sx={{ 
          height: '100vh', 
          width: '100%', 
          padding: { xs: 1, md: 3 },
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header with title, subtitle and Add Technician button */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography 
                variant="h4" 
                component="h1"
                sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '1.25rem', md: '2rem' },
                  color: 'text.primary',
                  mb: 0.5,
                }}
              >
                Technicians
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                }}
              >
                Manage your technicians and vendors
              </Typography>
            </Box>
            {canCreateTechnician(user) && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    boxShadow: 'none',
                  }}
                >
                  {/* Main button section */}
                  <Button
                    variant="contained"
                    onClick={() => handleOpenAddDialog('technician')}
                    sx={{
                      backgroundColor: isDarkMode ? '#fff' : '#000',
                      color: isDarkMode ? '#000' : '#fff',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      px: 3,
                      py: 1.25,
                      borderRadius: 0,
                      borderTopLeftRadius: 1.5,
                      borderBottomLeftRadius: 1.5,
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                        boxShadow: 'none',
                      }
                    }}
                  >
                    + Add Technician/Vendor
                  </Button>
                  {/* Dropdown arrow section */}
                  <Button
                    variant="contained"
                    onClick={handleMenuOpen}
                    sx={{
                      backgroundColor: isDarkMode ? '#fff' : '#000',
                      color: isDarkMode ? '#000' : '#fff',
                      minWidth: 'auto',
                      px: 1,
                      py: 1.25,
                      borderRadius: 0,
                      borderTopRightRadius: 1.5,
                      borderBottomRightRadius: 1.5,
                      borderLeft: isDarkMode ? '1px solid rgba(0, 0, 0, 0.2)' : '1px solid rgba(255, 255, 255, 0.2)',
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                        boxShadow: 'none',
                      }
                    }}
                  >
                    <ArrowDropDownIcon sx={{ fontSize: '1.25rem' }} />
                  </Button>
                </Box>
                {/* Dropdown menu */}
                <Menu
                  anchorEl={menuAnchorEl}
                  open={Boolean(menuAnchorEl)}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  PaperProps={{
                    sx: {
                      mt: 0.5,
                      minWidth: 150,
                      borderRadius: 1.5,
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                      backgroundColor: isDarkMode ? '#fff' : '#000',
                      overflow: 'hidden',
                    }
                  }}
                >
                  <MenuItem
                    onClick={() => handleMenuSelect('technician')}
                    sx={{
                      fontSize: '0.875rem',
                      py: 1.25,
                      color: isDarkMode ? '#000' : '#fff',
                      display: 'flex',
                      justifyContent: 'center',
                      borderBottom: isDarkMode ? '1px solid rgba(0, 0, 0, 0.2)' : '1px solid rgba(255, 255, 255, 0.2)',
                      '&:hover': {
                        backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                      }
                    }}
                  >
                    Technician
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleMenuSelect('vendor')}
                    sx={{
                      fontSize: '0.875rem',
                      py: 1.25,
                      color: isDarkMode ? '#000' : '#fff',
                      display: 'flex',
                      justifyContent: 'center',
                      '&:hover': {
                        backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                      }
                    }}
                  >
                    Vendor
                  </MenuItem>
                </Menu>
              </Box>
            )}
          </Box>
        </Box>

        {/* Summary boxes */}
        <Box sx={{
          display: 'flex',
          gap: 2,
          mb: 3,
          overflowX: { xs: 'auto', md: 'visible' },
          flexWrap: { xs: 'nowrap', md: 'wrap' },
          pb: { xs: 0.5, md: 0 },
        }}>
          {/* Total Technicians */}
          <Box
            onClick={() => handleFilterClick(null)}
            sx={{
              flex: { xs: '0 0 auto', md: 1 },
              minWidth: { xs: 140, md: 'auto' },
              backgroundColor: 'background.paper',
              borderRadius: 2,
              p: 2.5,
              boxShadow: isDarkMode ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)',
              cursor: 'pointer',
              border: activeFilter === null ? (isDarkMode ? '2px solid #fff' : '2px solid #000') : (isDarkMode ? '2px solid #334155' : '2px solid transparent'),
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: isDarkMode ? '0 4px 8px rgba(0, 0, 0, 0.4)' : '0 4px 8px rgba(0, 0, 0, 0.15)',
                transform: 'translateY(-2px)',
              }
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.875rem',
                mb: 1,
                fontWeight: 400,
              }}
            >
              Total Technicians
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: 'text.primary',
                fontSize: '2rem',
                fontWeight: 700,
                lineHeight: 1.2,
              }}
            >
              {summaryCounts.total}
            </Typography>
          </Box>

          {/* 1099 Required */}
          <Box
            onClick={() => handleFilterClick('1099')}
            sx={{
              flex: { xs: '0 0 auto', md: 1 },
              minWidth: { xs: 140, md: 'auto' },
              backgroundColor: 'background.paper',
              borderRadius: 2,
              p: 2.5,
              boxShadow: isDarkMode ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)',
              cursor: 'pointer',
              border: activeFilter === '1099' ? '2px solid #d32f2f' : (isDarkMode ? '2px solid #334155' : '2px solid transparent'),
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: isDarkMode ? '0 4px 8px rgba(0, 0, 0, 0.4)' : '0 4px 8px rgba(0, 0, 0, 0.15)',
                transform: 'translateY(-2px)',
              }
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.875rem',
                mb: 1,
                fontWeight: 400,
              }}
            >
              1099 Required
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: '#d32f2f',
                fontSize: '2rem',
                fontWeight: 700,
                lineHeight: 1.2,
              }}
            >
              {summaryCounts.count1099Required}
            </Typography>
          </Box>

          {/* Need Form W9 */}
          <Box
            onClick={() => handleFilterClick('w9')}
            sx={{
              flex: { xs: '0 0 auto', md: 1 },
              minWidth: { xs: 140, md: 'auto' },
              backgroundColor: 'background.paper',
              borderRadius: 2,
              p: 2.5,
              boxShadow: isDarkMode ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)',
              cursor: 'pointer',
              border: activeFilter === 'w9' ? '2px solid #ff9800' : (isDarkMode ? '2px solid #334155' : '2px solid transparent'),
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: isDarkMode ? '0 4px 8px rgba(0, 0, 0, 0.4)' : '0 4px 8px rgba(0, 0, 0, 0.15)',
                transform: 'translateY(-2px)',
              }
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.875rem',
                mb: 1,
                fontWeight: 400,
              }}
            >
              Need Form W9
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: '#ff9800',
                fontSize: '2rem',
                fontWeight: 700,
                lineHeight: 1.2,
              }}
            >
              {summaryCounts.countNeedW9}
            </Typography>
          </Box>

          {/* Blacklisted */}
          <Box
            onClick={() => handleFilterClick('blacklisted')}
            sx={{
              flex: { xs: '0 0 auto', md: 1 },
              minWidth: { xs: 140, md: 'auto' },
              backgroundColor: 'background.paper',
              borderRadius: 2,
              p: 2.5,
              boxShadow: isDarkMode ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)',
              cursor: 'pointer',
              border: activeFilter === 'blacklisted' ? '2px solid #d32f2f' : (isDarkMode ? '2px solid #334155' : '2px solid transparent'),
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: isDarkMode ? '0 4px 8px rgba(0, 0, 0, 0.4)' : '0 4px 8px rgba(0, 0, 0, 0.15)',
                transform: 'translateY(-2px)',
              }
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.875rem',
                mb: 1,
                fontWeight: 400,
              }}
            >
              Blacklisted
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: '#d32f2f',
                fontSize: '2rem',
                fontWeight: 700,
                lineHeight: 1.2,
              }}
            >
              {summaryCounts.countBlacklisted}
            </Typography>
          </Box>

          {/* Archived */}
          <Box
            onClick={() => handleFilterClick('archived')}
            sx={{
              flex: { xs: '0 0 auto', md: 1 },
              minWidth: { xs: 140, md: 'auto' },
              backgroundColor: 'background.paper',
              borderRadius: 2,
              p: 2.5,
              boxShadow: isDarkMode ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)',
              cursor: 'pointer',
              border: activeFilter === 'archived' ? '2px solid #9e9e9e' : (isDarkMode ? '2px solid #334155' : '2px solid transparent'),
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: isDarkMode ? '0 4px 8px rgba(0, 0, 0, 0.4)' : '0 4px 8px rgba(0, 0, 0, 0.15)',
                transform: 'translateY(-2px)',
              }
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.875rem',
                mb: 1,
                fontWeight: 400,
              }}
            >
              Archived
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: '#9e9e9e',
                fontSize: '2rem',
                fontWeight: 700,
                lineHeight: 1.2,
              }}
            >
              {summaryCounts.countArchived}
            </Typography>
          </Box>
        </Box>

        {/* Search bar */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search technicians/vendors by name, company, or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
                backgroundColor: 'background.paper',
                fontSize: '0.875rem',
                height: '42px',
                '& input::placeholder': {
                  color: 'text.secondary',
                  opacity: 1,
                },
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                  borderWidth: '1px',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#e0e0e0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#e0e0e0',
                  borderWidth: '1px',
                },
              }
            }}
          />
        </Box>

        {/* Filters and View Toggle - Same Line */}
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          gap: 2,
          flexWrap: 'wrap',
        }}>
          {/* Filters */}
          <Box sx={{ 
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
            flex: 1,
            flexWrap: 'wrap',
          }}>
            {/* Type Filter */}
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem', mr: 0.5 }}>
                Type:
              </Typography>
              <Chip
                label="Technician"
                onClick={() => handleTypeToggle('technician')}
                color={selectedTypes.includes('technician') ? 'primary' : 'default'}
                variant={selectedTypes.includes('technician') ? 'filled' : 'outlined'}
                sx={{
                  height: '32px',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  backgroundColor: selectedTypes.includes('technician') ? (isDarkMode ? '#fff' : '#000') : 'transparent',
                  color: selectedTypes.includes('technician') ? (isDarkMode ? '#000' : '#fff') : 'text.secondary',
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                  '&:hover': {
                    backgroundColor: selectedTypes.includes('technician') ? (isDarkMode ? '#e0e0e0' : '#333') : (isDarkMode ? '#1e293b' : '#f5f5f5'),
                  },
                }}
              />
              <Chip
                label="Vendor"
                onClick={() => handleTypeToggle('vendor')}
                color={selectedTypes.includes('vendor') ? 'primary' : 'default'}
                variant={selectedTypes.includes('vendor') ? 'filled' : 'outlined'}
                sx={{
                  height: '32px',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  backgroundColor: selectedTypes.includes('vendor') ? (isDarkMode ? '#fff' : '#000') : 'transparent',
                  color: selectedTypes.includes('vendor') ? (isDarkMode ? '#000' : '#fff') : 'text.secondary',
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                  '&:hover': {
                    backgroundColor: selectedTypes.includes('vendor') ? (isDarkMode ? '#e0e0e0' : '#333') : (isDarkMode ? '#1e293b' : '#f5f5f5'),
                  },
                }}
              />
            </Box>

            {/* State Filter */}
            <Autocomplete
              multiple
              size="small"
              options={availableStates}
              value={selectedStates}
              onChange={(event, newValue) => {
                setSelectedStates(newValue);
              }}
              getOptionLabel={(option) => states[option] || option}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="State"
                  sx={{
                    minWidth: { xs: '100%', sm: 150 },
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.875rem',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                    },
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...chipProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={states[option] || option}
                      size="small"
                      {...chipProps}
                      sx={{
                        '& .MuiChip-deleteIcon': {
                          fontSize: '16px',
                        },
                      }}
                    />
                  );
                })
              }
              renderOption={(props, option) => (
                <li {...props}>
                  {states[option] || option}
                </li>
              )}
              sx={{
                minWidth: { xs: '100%', sm: 150 },
              }}
            />

            {/* Year Filter */}
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
              <InputLabel id="year-filter-label">Year</InputLabel>
              <Select
                labelId="year-filter-label"
                id="year-filter"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                label="Year"
                disabled={loadingYearEarnings}
                endAdornment={loadingYearEarnings ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                sx={{
                  fontSize: '0.875rem',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                  },
                }}
              >
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year === 'all' ? 'All Years' : year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* View Toggle Buttons */}
          <Box sx={{ 
            display: 'flex',
            gap: 0.5,
            alignItems: 'center',
            backgroundColor: 'background.paper',
            borderRadius: 1,
            padding: 0.5,
            boxShadow: isDarkMode ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)',
            border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
          }}>
            {isMobile && viewType === 'list' && (
              <IconButton
                onClick={handleColumnsPanelToggle}
                size="small"
                aria-label="Manage columns"
                sx={{ width: 36, height: 36 }}
              >
                <ViewColumnIcon sx={{ fontSize: '1.25rem' }} />
              </IconButton>
            )}
            <IconButton
              onClick={() => setViewType('grid')}
              size="small"
              sx={{
                backgroundColor: viewType === 'grid' ? (isDarkMode ? '#1e293b' : '#fff') : 'transparent',
                color: viewType === 'grid' ? '#1976d2' : 'text.secondary',
                width: 36,
                height: 36,
                '&:hover': {
                  backgroundColor: isDarkMode ? '#1e293b' : '#f5f5f5',
                },
              }}
            >
              <GridViewIcon sx={{ fontSize: '1.25rem' }} />
            </IconButton>
            <IconButton
              onClick={() => setViewType('list')}
              size="small"
              sx={{
                backgroundColor: viewType === 'list' ? (isDarkMode ? '#1e293b' : '#fff') : 'transparent',
                color: viewType === 'list' ? '#1976d2' : 'text.secondary',
                width: 36,
                height: 36,
                '&:hover': {
                  backgroundColor: isDarkMode ? '#1e293b' : '#f5f5f5',
                },
              }}
            >
              <ViewListIcon sx={{ fontSize: '1.25rem' }} />
            </IconButton>
          </Box>
        </Box>

        {/* View Toggle and Content Container */}
        <Box sx={{ 
          backgroundColor: 'background.paper', 
          borderRadius: 2,
          border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
          overflow: 'hidden',
          position: 'relative',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          // Reserve right gutter so the columns panel does not cover grid/footer content.
          paddingRight: viewType === 'list' && !isMobile ? '48px' : 0,
        }}>
          
          {/* Columns Panel Button - Only show in list view on desktop */}
          {viewType === 'list' && !isMobile && (
            <Box
              sx={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                width: '48px',
                zIndex: 10,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                backgroundColor: 'background.paper',
                borderLeft: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
              }}
            >
              <Button
                onClick={handleColumnsPanelToggle}
                sx={{
                  minWidth: '48px',
                  width: '48px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  padding: '12px 0',
                  gap: 1,
                  backgroundColor: 'transparent',
                  color: 'text.primary',
                  borderRadius: 0,
                  '&:hover': {
                    backgroundColor: isDarkMode ? '#1e293b' : '#f5f5f5',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '3px',
                    backgroundColor: isDarkMode ? '#fff' : '#000',
                  },
                }}
                aria-label="Manage columns"
              >
                <ViewColumnIcon sx={{ fontSize: '20px', mb: 0.5, color: 'text.primary' }} />
                <Typography
                  sx={{
                    writingMode: 'vertical-rl',
                    textOrientation: 'mixed',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    letterSpacing: '0.5px',
                    color: 'text.primary',
                  }}
                >
                  Columns
                </Typography>
              </Button>
            </Box>
          )}

          {/* Grid View - Card Layout */}
          {viewType === 'grid' && (
            <Box sx={{ 
              p: 3,
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
            }}>
              {technicians.loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                  <CircularProgress />
                </Box>
              ) : filteredTechnicians.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    No technicians found
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ 
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                  gap: 2.5,
                }}>
                  {filteredTechnicians.map((tech) => {
                    const numEarnings = parseNumericField(tech.total_earnings);
                    const formattedEarnings = `$${numEarnings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                    const totalJobs = parseNumericField(tech.total_jobs);
                    const avgTicket = getAvgWorkOrderCost(tech.total_earnings, tech.total_jobs);
                    const formattedAvgTicket = `$${avgTicket.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                    const hasW9 = tech.w9 || false;
                    const earningsExceed1099Threshold = numEarnings > REQUIRED_1099_THRESHOLD;
                    const isBlacklisted = tech.blacklisted || false;
                    const isArchived = isTechnicianArchived(tech);
                    const displayName = getDisplayName(tech);
                    
                    // Build location string
                    const locationParts = [];
                    if (tech.city) locationParts.push(tech.city);
                    if (tech.state) locationParts.push(tech.state);
                    const locationStr = locationParts.join(', ');
                    const travelDistance = tech.travel_distance ? ` • ${tech.travel_distance} mi radius` : '';
                    const fullLocation = locationStr + travelDistance;

                    return (
                      <Card
                        key={tech.id}
                        onClick={() => handleCardClick(tech)}
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          border: isBlacklisted ? '2px solid #d32f2f' : isArchived ? '2px solid #9e9e9e' : (isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0'),
                          bgcolor: 'background.paper',
                          boxShadow: isDarkMode ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.12)',
                          '&:hover': {
                            boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.4)' : '0 4px 12px rgba(0, 0, 0, 0.15)',
                            transform: 'translateY(-2px)',
                          },
                        }}
                      >
                        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                          {/* Name and Edit Icon */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                fontSize: '1.125rem',
                                color: 'text.primary',
                                flex: 1,
                              }}
                            >
                              {displayName || 'N/A'}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={(e) => handleEditClick(e, tech)}
                              sx={{
                                padding: 0.5,
                                '&:hover': {
                                  backgroundColor: isDarkMode ? '#1e293b' : '#f5f5f5',
                                },
                              }}
                            >
                              <EditIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                            </IconButton>
                          </Box>

                          {/* W9 Status Badge and Blacklisted Indicator */}
                          <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Box
                              sx={{
                                display: 'inline-block',
                                padding: '4px 12px',
                                borderRadius: '12px',
                                backgroundColor: hasW9 ? '#8FC98F' : '#FFD54F',
                                border: `1px solid ${hasW9 ? '#5A985A' : '#FBC02D'}`,
                                color: '#fff',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                              }}
                            >
                              {hasW9 ? 'W9 Attached' : 'W9 Required'}
                            </Box>
                            {isArchived && (
                              <Box
                                sx={{
                                  display: 'inline-block',
                                  padding: '4px 12px',
                                  borderRadius: '12px',
                                  backgroundColor: isDarkMode ? '#475569' : '#e0e0e0',
                                  border: `1px solid ${isDarkMode ? '#64748b' : '#bdbdbd'}`,
                                  color: isDarkMode ? '#e2e8f0' : '#616161',
                                  fontWeight: 500,
                                  fontSize: '0.75rem',
                                }}
                              >
                                Archived
                              </Box>
                            )}
                            {isBlacklisted && (
                              <Box
                                sx={{
                                  display: 'inline-block',
                                  padding: '4px 12px',
                                  borderRadius: '12px',
                                  backgroundColor: '#d32f2f',
                                  border: '1px solid #b71c1c',
                                  color: '#fff',
                                  fontWeight: 500,
                                  fontSize: '0.75rem',
                                }}
                              >
                                Blacklisted
                              </Box>
                            )}
                          </Box>

                          {/* Contact Information */}
                          <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <EmailIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                                {tech.email || 'N/A'}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PhoneIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                                {tech.phone || 'N/A'}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LocationOnIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                                {fullLocation || 'N/A'}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Financial/Job Data */}
                          <Box sx={{ 
                            display: 'grid', 
                            gridTemplateColumns: '1fr 1fr 1fr',
                            gap: 1.5,
                            mb: 2,
                            pb: 2,
                            borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #f0f0f0',
                          }}>
                            <Box>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                Avg Ticket
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.875rem' }}>
                                {formattedAvgTicket}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                Total Earnings
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: earningsExceed1099Threshold ? '#ff9800' : 'text.primary', 
                                  fontWeight: 600, 
                                  fontSize: '0.875rem' 
                                }}
                              >
                                {formattedEarnings}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                Total Work Orders
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.875rem' }}>
                                {totalJobs}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Request W9 Button - Removed for now */}
                          {/* <Button
                            variant="contained"
                            startIcon={<DescriptionIcon />}
                            fullWidth
                            onClick={(e) => {
                              e.stopPropagation();
                              // Handle request W9 action
                            }}
                            sx={{
                              backgroundColor: '#000',
                              color: '#fff',
                              textTransform: 'none',
                              fontWeight: 500,
                              fontSize: '0.875rem',
                              py: 1,
                              '&:hover': {
                                backgroundColor: '#333',
                              },
                            }}
                          >
                            Request W9
                          </Button> */}

                          {/* Earnings Alert */}
                          {earningsExceed1099Threshold && (
                            <Box
                              sx={{
                                mt: 1.5,
                                p: 1,
                                backgroundColor: isDarkMode ? '#7c2d12' : '#FFF3E0',
                                borderRadius: 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <WarningIcon sx={{ fontSize: '1rem', color: '#ff9800' }} />
                              <Typography variant="caption" sx={{ color: isDarkMode ? '#fdba74' : '#E65100', fontSize: '0.75rem', fontWeight: 500 }}>
                                {`Earnings exceed $${REQUIRED_1099_THRESHOLD.toLocaleString('en-US')} threshold`}
                              </Typography>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}

          {/* List View - Data Grid */}
          {viewType === 'list' && (
          <DataGrid
            apiRef={gridApiRef}
            rows={filteredTechnicians}
            loading={technicians.loading}
            columns={displayColumns}
            density={isMobile ? 'compact' : 'standard'}
            initialState={{ pagination: { paginationModel } }}
            pageSizeOptions={[5, 10, 20, 30, 50, 100]}
            onRowSelectionModelChange={handleRowSelectionModelChange}
            getRowClassName={(params) => {
              if (isTechnicianArchived(params.row)) return 'row-archived';
              const earnings = params.row.total_earnings;
              const numEarnings = earnings == null || earnings === undefined ? 0 : (typeof earnings === 'string' ? parseFloat(earnings) : earnings);
              return numEarnings > REQUIRED_1099_THRESHOLD ? 'row-1099-required' : '';
            }}
            sx={{ 
              border: 0,
              height: '100%',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
              },
              '& .MuiDataGrid-columnHeaderTitle': { 
                fontWeight: 600,
                fontSize: '0.8125rem',
                color: 'text.secondary',
              },
              '& .MuiDataGrid-cell': { 
                fontSize: '0.875rem',
                color: 'text.primary',
                borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #f5f5f5',
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiDataGrid-row': {
                '&:hover': {
                  backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                },
                '&.row-archived': {
                  backgroundColor: isDarkMode ? '#334155' : '#f0f0f0',
                  '&:hover': {
                    backgroundColor: isDarkMode ? '#475569' : '#e8e8e8',
                  },
                },
                '&.row-1099-required': {
                  backgroundColor: isDarkMode ? '#7f1d1d' : '#ffebee',
                  '&:hover': {
                    backgroundColor: isDarkMode ? '#991b1b' : '#ffcdd2',
                  },
                },
              },
              '& .MuiDataGrid-footerContainer': { 
                borderTop: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                position: 'relative',
                zIndex: 2,
              },
            }}
          />
          )}
        </Box>
      </Paper>

      {/* Technician details dialog */}
      <TechnicianDetailsDialog selectedTechnician={selectedTechnician} setSelectedTechnician={setSelectedTechnician} user={user} />
      
      {/* Add technician dialog */}
      {canCreateTechnician(user) && (
        <AddTechnicianDialog 
          open={addTechnicianDialogOpen} 
          onClose={() => setAddTechnicianDialogOpen(false)} 
          onTechnicianAdded={() => setAddTechnicianDialogOpen(false)}
          initialType={initialTechnicianType}
        />
      )}

      {/* Blacklist reason dialog */}
      <Dialog
        open={blacklistDialogOpen}
        onClose={handleBlacklistDialogCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1.25rem', pb: 1, color: 'text.primary' }}>
          Blacklist Technician
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Please provide a reason for blacklisting {blacklistDialogTechnician && getDisplayName(blacklistDialogTechnician)}. This reason is required and must be at least 10 characters.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Blocked Reason"
            value={blockedReason}
            onChange={(e) => {
              setBlockedReason(e.target.value);
              if (blockedReasonError) {
                setBlockedReasonError('');
              }
            }}
            error={!!blockedReasonError}
            helperText={blockedReasonError || 'Minimum 10 characters required'}
            placeholder="Enter the reason for blacklisting this technician..."
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={handleBlacklistDialogCancel}
            sx={{ 
              color: 'text.secondary',
              textTransform: 'none',
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleBlacklistDialogConfirm}
            variant="contained"
            disabled={!blockedReason || blockedReason.trim().length < 10}
            sx={{ 
              backgroundColor: isDarkMode ? '#fff' : '#000',
              color: isDarkMode ? '#000' : 'white',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
              },
              '&:disabled': {
                backgroundColor: isDarkMode ? '#475569' : '#ccc',
                color: isDarkMode ? '#94a3b8' : '#666',
              }
            }}
          >
            Confirm Blacklist
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
